import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, Eye, Users, BookmarkSimple, Certificate, PencilSimple,
  VideoCamera, Plus, Trash, Crown, X, Check,
  ChartBar, SignOut, ChatCircle, File, PushPin, UserCircle,
  ArrowSquareOut, MagnifyingGlass, CaretDown, Robot
} from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIDEO_CATEGORIES = [
  { id: 'mindset', name: 'Mindset & Motivation' },
  { id: 'wealth_building', name: 'Wealth Building' },
  { id: 'business_strategy', name: 'Business Strategy' },
  { id: 'brotherhood_sessions', name: 'Brotherhood Sessions' },
  { id: 'inner_circle_exclusives', name: 'Inner Circle Exclusives' }
];

const ROLE_OPTIONS = [
  { value: 'guest', label: 'Guest', color: 'text-gray-400' },
  { value: 'free_member', label: 'Free Member', color: 'text-blue-400' },
  { value: 'paid_member', label: 'Paid Member', color: 'text-[#c9a227]' },
  { value: 'admin', label: 'Admin', color: 'text-red-400' }
];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  
  // Data States
  const [videos, setVideos] = useState([]);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [pages, setPages] = useState([]);
  
  // Modal States
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: '', name: '' });
  
  // Search States
  const [memberSearch, setMemberSearch] = useState('');
  const [postSearch, setPostSearch] = useState('');
  
  // Form States
  const [videoForm, setVideoForm] = useState({
    title: '', description: '', youtube_url: '', category: 'mindset',
    is_premium: false, is_featured: false, order: 0
  });
  const [pageForm, setPageForm] = useState({
    title: '', slug: '', content: '', is_published: true, meta_description: ''
  });

  useEffect(() => {
    const savedToken = localStorage.getItem('powr-admin-token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      fetchAllData(savedToken);
    }
  }, []);

  const fetchAllData = async (authToken) => {
    try {
      const [statsRes, videosRes, membersRes, postsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/videos`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/members`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/posts`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/pages`, { headers: { Authorization: `Bearer ${authToken}` } }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setVideos(videosRes.data);
      setMembers(membersRes.data);
      setPosts(postsRes.data);
      setPages(pagesRes.data);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      const newToken = response.data.access_token;
      setToken(newToken);
      localStorage.setItem('powr-admin-token', newToken);
      setIsAuthenticated(true);
      toast.success('Welcome, Admin');
      fetchAllData(newToken);
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    localStorage.removeItem('powr-admin-token');
    navigate('/');
  };

  // ===== VIDEO HANDLERS =====
  const resetVideoForm = () => {
    setVideoForm({ title: '', description: '', youtube_url: '', category: 'mindset', is_premium: false, is_featured: false, order: 0 });
    setEditingVideo(null);
    setShowAddVideo(false);
  };

  const handleSaveVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.title || !videoForm.youtube_url) {
      toast.error('Title and YouTube URL are required');
      return;
    }
    setLoading(true);
    try {
      if (editingVideo) {
        await axios.put(`${API}/admin/videos/${editingVideo.id}`, videoForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Video updated');
      } else {
        await axios.post(`${API}/admin/videos`, videoForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Video added');
      }
      resetVideoForm();
      fetchAllData(token);
    } catch (error) {
      toast.error('Failed to save video');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVideo = (video) => {
    setVideoForm({
      title: video.title, description: video.description || '', youtube_url: video.youtube_url,
      category: video.category, is_premium: video.is_premium, is_featured: video.is_featured, order: video.order
    });
    setEditingVideo(video);
    setShowAddVideo(true);
  };

  const handleDeleteVideo = async (videoId, videoTitle) => {
    setDeleteConfirm({ show: true, type: 'video', id: videoId, name: videoTitle || 'this video' });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteConfirm;
    try {
      if (type === 'video') {
        await axios.delete(`${API}/admin/videos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Video deleted');
      } else if (type === 'member') {
        await axios.delete(`${API}/admin/members/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Member deleted');
      } else if (type === 'post') {
        await axios.delete(`${API}/admin/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Post deleted');
      } else if (type === 'page') {
        await axios.delete(`${API}/pages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Page deleted');
      }
      fetchAllData(token);
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    } finally {
      setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    }
  };

  // ===== MEMBER HANDLERS =====
  const handleUpdateMemberRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/members/${userId}/role?role=${newRole}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Member role updated');
      fetchAllData(token);
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteMember = async (userId, memberName) => {
    setDeleteConfirm({ show: true, type: 'member', id: userId, name: memberName });
  };

  // ===== POST HANDLERS =====
  const handleDeletePost = async (postId, postContent) => {
    setDeleteConfirm({ show: true, type: 'post', id: postId, name: postContent?.substring(0, 50) + '...' || 'this post' });
  };

  const handleTogglePinPost = async (postId) => {
    try {
      const response = await axios.put(`${API}/admin/posts/${postId}/pin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      fetchAllData(token);
    } catch (error) {
      toast.error('Failed to pin/unpin post');
    }
  };

  // ===== PAGE HANDLERS =====
  const resetPageForm = () => {
    setPageForm({ title: '', slug: '', content: '', is_published: true, meta_description: '' });
    setEditingPage(null);
    setShowAddPage(false);
  };

  const handleSavePage = async (e) => {
    e.preventDefault();
    if (!pageForm.title || !pageForm.slug || !pageForm.content) {
      toast.error('Title, slug, and content are required');
      return;
    }
    setLoading(true);
    try {
      if (editingPage) {
        await axios.put(`${API}/admin/pages/${editingPage.id}`, pageForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Page updated');
      } else {
        await axios.post(`${API}/admin/pages`, pageForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Page created');
      }
      resetPageForm();
      fetchAllData(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPage = (page) => {
    setPageForm({
      title: page.title, slug: page.slug, content: page.content,
      is_published: page.is_published, meta_description: page.meta_description || ''
    });
    setEditingPage(page);
    setShowAddPage(true);
  };

  const handleDeletePage = async (pageId, pageTitle) => {
    setDeleteConfirm({ show: true, type: 'page', id: pageId, name: pageTitle || 'this page' });
  };

  // Filter functions
  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(memberSearch.toLowerCase())
  );
  const filteredPosts = posts.filter(p => 
    p.content?.toLowerCase().includes(postSearch.toLowerCase()) ||
    p.author_name?.toLowerCase().includes(postSearch.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="p-8 bg-[#111111] rounded-none border border-[#222]">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#c9a227]/10 flex items-center justify-center">
                <Lock size={32} weight="duotone" className="text-[#c9a227]" />
              </div>
              <h1 className="text-3xl font-black text-white mb-2">Admin Dashboard</h1>
              <p className="text-sm text-white/50">Enter your credentials to continue</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-white/70">Email / Username</Label>
                <Input
                  data-testid="admin-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white"
                  placeholder="greg.cornelius2023@gmail.com"
                  required
                />
              </div>
              <div>
                <Label className="text-white/70">Password</Label>
                <Input
                  data-testid="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white"
                  placeholder="Enter password"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loading}
                className="w-full rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold"
              >
                {loading ? 'Logging in...' : 'Access Dashboard'}
              </Button>
            </form>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#111111] border-r border-[#222] z-40 hidden md:block">
        <div className="p-6">
          <h1 className="text-2xl font-black text-[#c9a227]">POWR</h1>
          <p className="text-xs text-white/40 mt-1">Admin Dashboard</p>
        </div>
        
        <nav className="px-4 space-y-1">
          <SidebarItem icon={ChartBar} label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
          <SidebarItem icon={VideoCamera} label="Videos" active={activeTab === 'videos'} onClick={() => setActiveTab('videos')} />
          <SidebarItem icon={ChatCircle} label="Posts" active={activeTab === 'posts'} onClick={() => setActiveTab('posts')} />
          <SidebarItem icon={Users} label="Members" active={activeTab === 'members'} onClick={() => setActiveTab('members')} />
          <SidebarItem icon={File} label="Pages" active={activeTab === 'pages'} onClick={() => setActiveTab('pages')} />
          <div className="pt-4 mt-4 border-t border-[#222]">
            <button
              onClick={() => navigate('/admin/seeding')}
              className="flex items-center gap-3 w-full px-4 py-3 text-white/60 hover:text-white hover:bg-white/5 transition-colors rounded"
            >
              <Robot size={20} className="text-[#8B5CF6]" />
              <span>Community Seeding</span>
            </button>
          </div>
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#222]">
          <button
            onClick={() => navigate('/community')}
            className="flex items-center gap-3 w-full px-4 py-2 mb-2 text-white/60 hover:text-white hover:bg-white/5 transition-colors rounded"
          >
            <ArrowSquareOut size={20} />
            <span>View Site</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 transition-colors rounded"
          >
            <SignOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#111111] border-b border-[#222] z-50 md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-black text-[#c9a227]">POWR Admin</h1>
          <button onClick={handleLogout} className="p-2 text-white/60"><SignOut size={20} /></button>
        </div>
        <div className="flex overflow-x-auto border-t border-[#222]">
          {['overview', 'videos', 'posts', 'members', 'pages'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-medium capitalize ${activeTab === tab ? 'text-[#c9a227] border-b-2 border-[#c9a227]' : 'text-white/60'}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 pt-28 md:pt-0 min-h-screen">
        <div className="p-6 md:p-8">
          
          {/* ===== OVERVIEW TAB ===== */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-black mb-2">Dashboard Overview</h2>
                <p className="text-white/50">Platform statistics at a glance</p>
              </div>
              
              {stats && (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={Users} value={stats.total_users} label="Total Members" color="blue" />
                  <StatCard icon={Crown} value={stats.total_paid_members} label="Paid Members" color="gold" />
                  <StatCard icon={VideoCamera} value={videos.length} label="Videos" color="purple" />
                  <StatCard icon={ChatCircle} value={posts.length} label="Posts" color="green" />
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6 bg-[#111111] border-[#222] rounded-none">
                  <h3 className="text-lg font-bold mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button onClick={() => { setActiveTab('videos'); setShowAddVideo(true); }} className="w-full flex items-center gap-3 px-4 py-3 bg-[#c9a227]/10 hover:bg-[#c9a227]/20 text-[#c9a227] transition-colors">
                      <Plus size={20} /><span>Add New Video</span>
                    </button>
                    <button onClick={() => { setActiveTab('pages'); setShowAddPage(true); }} className="w-full flex items-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/10 text-white transition-colors">
                      <File size={20} /><span>Create New Page</span>
                    </button>
                    <button onClick={() => navigate('/videos')} className="w-full flex items-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/10 text-white transition-colors">
                      <Eye size={20} /><span>View Video Library</span>
                    </button>
                  </div>
                </Card>
                
                <Card className="p-6 bg-[#111111] border-[#222] rounded-none">
                  <h3 className="text-lg font-bold mb-4">Recent Members</h3>
                  <div className="space-y-3">
                    {members.slice(0, 5).map(member => (
                      <div key={member.id} className="flex items-center justify-between text-sm">
                        <span className="text-white/80">{member.name}</span>
                        <span className={ROLE_OPTIONS.find(r => r.value === member.role)?.color || 'text-white/50'}>
                          {member.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* ===== VIDEOS TAB ===== */}
          {activeTab === 'videos' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-black mb-2">Video Library</h2>
                  <p className="text-white/50">Manage your curated video content</p>
                </div>
                <Button onClick={() => setShowAddVideo(true)} data-testid="add-video-btn" className="rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold flex items-center gap-2">
                  <Plus size={20} />Add Video
                </Button>
              </div>
              
              {VIDEO_CATEGORIES.map((category) => {
                const categoryVideos = videos.filter(v => v.category === category.id);
                if (categoryVideos.length === 0) return null;
                return (
                  <div key={category.id} className="space-y-3">
                    <h3 className="text-lg font-bold text-white/80">{category.name}</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoryVideos.map((video) => (
                        <VideoAdminCard key={video.id} video={video} onEdit={() => handleEditVideo(video)} onDelete={() => handleDeleteVideo(video.id, video.title)} />
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {videos.length === 0 && (
                <EmptyState icon={VideoCamera} title="No Videos Yet" description="Start building your video library" action={() => setShowAddVideo(true)} actionLabel="Add Your First Video" />
              )}
            </div>
          )}

          {/* ===== POSTS TAB ===== */}
          {activeTab === 'posts' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-black mb-2">Community Posts</h2>
                  <p className="text-white/50">Moderate and manage community content</p>
                </div>
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    value={postSearch}
                    onChange={(e) => setPostSearch(e.target.value)}
                    placeholder="Search posts..."
                    className="pl-10 rounded-none bg-[#0a0a0a] border-[#333] text-white w-64"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                {filteredPosts.map(post => (
                  <Card key={post.id} className="p-4 bg-[#111111] border-[#222] rounded-none">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-white">{post.author_name}</span>
                          <span className="text-xs text-white/40">{post.section}</span>
                          {post.pinned && <span className="text-xs bg-[#c9a227] text-black px-2 py-0.5 font-bold">PINNED</span>}
                        </div>
                        <p className="text-white/70 text-sm line-clamp-3">{post.content}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                          <span>{post.likes || 0} likes</span>
                          <span>{post.comment_count || 0} comments</span>
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleTogglePinPost(post.id)} className={`p-2 transition-colors ${post.pinned ? 'text-[#c9a227]' : 'text-white/40 hover:text-[#c9a227]'}`} title={post.pinned ? 'Unpin' : 'Pin'}>
                          <PushPin size={18} weight={post.pinned ? 'fill' : 'regular'} />
                        </button>
                        <button onClick={() => handleDeletePost(post.id, post.content)} className="p-2 text-white/40 hover:text-red-400 transition-colors" title="Delete">
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {posts.length === 0 && (
                <EmptyState icon={ChatCircle} title="No Posts Yet" description="Community posts will appear here" />
              )}
            </div>
          )}

          {/* ===== MEMBERS TAB ===== */}
          {activeTab === 'members' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-black mb-2">Members</h2>
                  <p className="text-white/50">Manage member accounts and roles</p>
                </div>
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search members..."
                    className="pl-10 rounded-none bg-[#0a0a0a] border-[#333] text-white w-64"
                  />
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-white/50 text-sm border-b border-[#222]">
                      <th className="pb-3 font-medium">Name</th>
                      <th className="pb-3 font-medium">Email</th>
                      <th className="pb-3 font-medium">Role</th>
                      <th className="pb-3 font-medium">Joined</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map(member => (
                      <tr key={member.id} className="border-b border-[#222]/50">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                              <UserCircle size={20} className="text-[#c9a227]" />
                            </div>
                            <span className="font-medium text-white">{member.name}</span>
                          </div>
                        </td>
                        <td className="py-4 text-white/60">{member.email}</td>
                        <td className="py-4">
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                            className="bg-[#0a0a0a] border border-[#333] text-white px-2 py-1 text-sm outline-none"
                          >
                            {ROLE_OPTIONS.map(role => (
                              <option key={role.value} value={role.value}>{role.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-4 text-white/40 text-sm">{new Date(member.created_at).toLocaleDateString()}</td>
                        <td className="py-4">
                          <button onClick={() => handleDeleteMember(member.id, member.name)} className="p-2 text-white/40 hover:text-red-400 transition-colors" title="Delete member">
                            <Trash size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {members.length === 0 && (
                <EmptyState icon={Users} title="No Members Yet" description="Members will appear here once they sign up" />
              )}
            </div>
          )}

          {/* ===== PAGES TAB ===== */}
          {activeTab === 'pages' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-black mb-2">Custom Pages</h2>
                  <p className="text-white/50">Create and manage custom content pages</p>
                </div>
                <Button onClick={() => setShowAddPage(true)} data-testid="add-page-btn" className="rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold flex items-center gap-2">
                  <Plus size={20} />Create Page
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pages.map(page => (
                  <Card key={page.id} className="bg-[#111111] border-[#222] rounded-none overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-white">{page.title}</h4>
                        {page.is_published ? (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5">Published</span>
                        ) : (
                          <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5">Draft</span>
                        )}
                      </div>
                      <p className="text-xs text-white/40 mb-3">/{page.slug}</p>
                      <p className="text-sm text-white/60 line-clamp-2 mb-4">{page.content.substring(0, 100)}...</p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleEditPage(page)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm transition-colors">
                          <PencilSimple size={14} />Edit
                        </button>
                        <button onClick={() => window.open(`/page/${page.slug}`, '_blank')} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm transition-colors">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => handleDeletePage(page.id, page.title)} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm transition-colors">
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {pages.length === 0 && (
                <EmptyState icon={File} title="No Custom Pages" description="Create custom pages for your site" action={() => setShowAddPage(true)} actionLabel="Create Your First Page" />
              )}
            </div>
          )}
        </div>
      </main>

      {/* ===== VIDEO MODAL ===== */}
      <AnimatePresence>
        {showAddVideo && (
          <Modal title={editingVideo ? 'Edit Video' : 'Add New Video'} onClose={resetVideoForm}>
            <form onSubmit={handleSaveVideo} className="space-y-4">
              <div>
                <Label className="text-white/70">Video Title *</Label>
                <Input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white" placeholder="Enter video title" data-testid="video-title-input" required />
              </div>
              <div>
                <Label className="text-white/70">YouTube URL *</Label>
                <Input value={videoForm.youtube_url} onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white" placeholder="https://youtube.com/watch?v=..." data-testid="video-url-input" required />
              </div>
              <div>
                <Label className="text-white/70">Description</Label>
                <Textarea value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white min-h-[80px]" placeholder="Brief description" />
              </div>
              <div>
                <Label className="text-white/70">Category</Label>
                <select value={videoForm.category} onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })} className="w-full mt-2 px-3 py-2 bg-[#0a0a0a] border border-[#333] text-white outline-none">
                  {VIDEO_CATEGORIES.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={videoForm.is_premium} onChange={(e) => setVideoForm({ ...videoForm, is_premium: e.target.checked })} className="w-4 h-4 accent-[#c9a227]" />
                  <span className="text-sm text-white/70">Premium (Paid Only)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={videoForm.is_featured} onChange={(e) => setVideoForm({ ...videoForm, is_featured: e.target.checked })} className="w-4 h-4 accent-[#c9a227]" />
                  <span className="text-sm text-white/70">Featured</span>
                </label>
              </div>
              <div className="flex items-center gap-3 pt-4">
                <Button type="button" onClick={resetVideoForm} className="flex-1 rounded-none bg-white/10 hover:bg-white/20 text-white">Cancel</Button>
                <Button type="submit" disabled={loading} data-testid="save-video-btn" className="flex-1 rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold">
                  {loading ? 'Saving...' : editingVideo ? 'Update Video' : 'Add Video'}
                </Button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* ===== PAGE MODAL ===== */}
      <AnimatePresence>
        {showAddPage && (
          <Modal title={editingPage ? 'Edit Page' : 'Create New Page'} onClose={resetPageForm}>
            <form onSubmit={handleSavePage} className="space-y-4">
              <div>
                <Label className="text-white/70">Page Title *</Label>
                <Input value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white" placeholder="e.g., About Us" required />
              </div>
              <div>
                <Label className="text-white/70">URL Slug * <span className="text-white/40">(lowercase, no spaces)</span></Label>
                <Input value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white" placeholder="about-us" required disabled={!!editingPage} />
                {pageForm.slug && <p className="text-xs text-white/40 mt-1">URL: /page/{pageForm.slug}</p>}
              </div>
              <div>
                <Label className="text-white/70">Content * <span className="text-white/40">(supports basic HTML)</span></Label>
                <Textarea value={pageForm.content} onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white min-h-[200px] font-mono text-sm" placeholder="Page content..." required />
              </div>
              <div>
                <Label className="text-white/70">Meta Description</Label>
                <Input value={pageForm.meta_description} onChange={(e) => setPageForm({ ...pageForm, meta_description: e.target.value })} className="rounded-none mt-2 bg-[#0a0a0a] border-[#333] text-white" placeholder="Brief description for SEO" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={pageForm.is_published} onChange={(e) => setPageForm({ ...pageForm, is_published: e.target.checked })} className="w-4 h-4 accent-[#c9a227]" />
                <span className="text-sm text-white/70">Publish immediately</span>
              </div>
              <div className="flex items-center gap-3 pt-4">
                <Button type="button" onClick={resetPageForm} className="flex-1 rounded-none bg-white/10 hover:bg-white/20 text-white">Cancel</Button>
                <Button type="submit" disabled={loading} className="flex-1 rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold">
                  {loading ? 'Saving...' : editingPage ? 'Update Page' : 'Create Page'}
                </Button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* ===== DELETE CONFIRMATION MODAL ===== */}
      <AnimatePresence>
        {deleteConfirm.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirm({ show: false, type: '', id: '', name: '' })}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#111111] border border-[#333] p-6 w-full max-w-md"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/10 flex items-center justify-center">
                  <Trash size={24} className="text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Confirm Delete</h3>
                  <p className="text-sm text-white/50">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-white/70 mb-6">
                Are you sure you want to delete <span className="text-white font-medium">"{deleteConfirm.name}"</span>?
                {deleteConfirm.type === 'member' && (
                  <span className="block mt-2 text-red-400/80 text-sm">
                    This will also delete all their posts and comments.
                  </span>
                )}
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setDeleteConfirm({ show: false, type: '', id: '', name: '' })}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-none"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-none"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ===== HELPER COMPONENTS =====

const SidebarItem = ({ icon: Icon, label, active, onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-colors ${active ? 'bg-[#c9a227]/10 text-[#c9a227]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
    <Icon size={20} weight={active ? 'fill' : 'regular'} />
    <span className="font-medium">{label}</span>
  </button>
);

const StatCard = ({ icon: Icon, value, label, color }) => {
  const colors = { blue: 'bg-blue-500/10 text-blue-400', gold: 'bg-[#c9a227]/10 text-[#c9a227]', purple: 'bg-purple-500/10 text-purple-400', green: 'bg-green-500/10 text-green-400' };
  return (
    <Card className="p-5 bg-[#111111] border-[#222] rounded-none">
      <div className={`w-10 h-10 ${colors[color]} flex items-center justify-center mb-3`}><Icon size={20} weight="duotone" /></div>
      <p className="text-3xl font-black text-white mb-1">{value}</p>
      <p className="text-sm text-white/50">{label}</p>
    </Card>
  );
};

const VideoAdminCard = ({ video, onEdit, onDelete }) => (
  <Card className="bg-[#111111] border-[#222] rounded-none overflow-hidden">
    <div className="relative aspect-video">
      <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
      {video.is_premium && <div className="absolute top-2 right-2 bg-[#c9a227] px-2 py-0.5 flex items-center gap-1"><Crown size={12} weight="fill" className="text-black" /><span className="text-[10px] font-bold text-black">PREMIUM</span></div>}
      {video.is_featured && <div className="absolute top-2 left-2 bg-white px-2 py-0.5"><span className="text-[10px] font-bold text-black">FEATURED</span></div>}
    </div>
    <div className="p-4">
      <h4 className="font-bold text-white line-clamp-1 mb-1">{video.title}</h4>
      <p className="text-xs text-white/50 mb-3">{video.category_display}</p>
      <div className="flex items-center gap-2">
        <button onClick={onEdit} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"><PencilSimple size={14} />Edit</button>
        <button onClick={onDelete} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm transition-colors"><Trash size={14} /></button>
      </div>
    </div>
  </Card>
);

const Modal = ({ title, onClose, children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="w-full max-w-xl bg-[#111111] border border-[#222] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black">{title}</h2>
        <button onClick={onClose} className="p-2 hover:bg-white/10 transition-colors"><X size={20} /></button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);

const EmptyState = ({ icon: Icon, title, description, action, actionLabel }) => (
  <div className="text-center py-16">
    <Icon size={64} className="mx-auto text-white/20 mb-4" />
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-white/50 mb-6">{description}</p>
    {action && <Button onClick={action} className="rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold">{actionLabel}</Button>}
  </div>
);

export default AdminDashboardPage;
